import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, InputGroup, OverlayTrigger } from 'react-bootstrap';
import BrregPopover from 'components/BrregPopover';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { OrgNrInfo } from './types';
import Style from '../../index.module.css';
import { CompareStatus } from '../../../../utils';
import { BrregWithDagligLeder } from 'utils/hooks/useBrregLookup';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import { EnhetSearchFilter } from 'services/KjederegisteretAdminApi/EnhetSearchApi/types';
import querystring from 'query-string';

interface Props {
  incoming: OrgNrInfo;
  compareStatus: CompareStatus;
  brregInfo?: BrregWithDagligLeder;
  onAttentionChange?: (needsAttention: boolean) => void;
}

const OrgNrLeft = ({ incoming, compareStatus, brregInfo, onAttentionChange }: Props) => {
  const [nameMismatch, setNameMismatch] = useState(false);
  const [enhetCount, setEnhetCount] = useState<number | undefined>(undefined);
  const [currentOrgnr, setCurrentOrgnr] = useState<string | undefined>(undefined);
  const lastCountQueryIdRef = useRef(0);

  useEffect(() => {
    const incomingName = incoming.juridisknavn;
    const brregName = brregInfo?.name ?? '';
    const mismatch = Boolean(
      incomingName &&
      brregName &&
      incomingName.trim().toUpperCase() !== brregName.trim().toUpperCase(),
    );
    setNameMismatch(mismatch);
  }, [incoming.juridisknavn, brregInfo]);

  useEffect(() => {
    const invalid = Boolean(brregInfo?.finnesIkke || brregInfo?.slettedato);
    const needsAttention =
      compareStatus === 'ulik' ||
      compareStatus === 'ny' ||
      invalid ||
      nameMismatch ||
      brregInfo?.finnesIkke ||
      !!brregInfo?.slettedato;
    onAttentionChange?.(needsAttention);
  }, [compareStatus, brregInfo, nameMismatch, onAttentionChange]);

  // Fetch count of enheter connected to incoming orgnr
  useEffect(() => {
    const trimmed = (incoming.organisasjonsnr ?? '').trim();
    setCurrentOrgnr(trimmed);
    setEnhetCount(undefined);
    if (!/^\d{9}$/.test(trimmed)) return;
    const queryId = ++lastCountQueryIdRef.current;
    EnhetApi.getCountByOrgnr(trimmed)
      .then((count) => {
        if (lastCountQueryIdRef.current === queryId) setEnhetCount(count);
      })
      // eslint-disable-next-line no-console
      .catch((err) => {
        if (lastCountQueryIdRef.current === queryId) setEnhetCount(undefined);
        console.error('Feil ved henting av antall enheter for orgnr', err);
      });
  }, [incoming.organisasjonsnr]);

  const overlay = brregInfo ? (
    <BrregPopover data={brregInfo} dagligLeder={brregInfo?.dagligLeder} />
  ) : (
    <></>
  );

  const getBrregButtonVariant = (): string => {
    if (brregInfo?.slettedato || brregInfo?.finnesIkke) return 'warning';
    return 'success';
  };

  return (
    <Col xs={12} md={5}>
      <Form.Group className={Style.formGroup}>
        <InputGroup size="sm" className={Style.orgNrInputGroup}>
          {!brregInfo ? (
            <InputGroup.Text style={{ paddingLeft: '.25rem', paddingRight: '.25rem' }}>
              <IcebergIcon icon="search" />
            </InputGroup.Text>
          ) : (
            <OverlayTrigger rootClose trigger="click" placement="bottom" overlay={overlay}>
              <Button
                variant={getBrregButtonVariant()}
                style={{ paddingLeft: '.25rem', paddingRight: '.25rem' }}
              >
                <IcebergIcon icon="check" bold />
              </Button>
            </OverlayTrigger>
          )}
          <Form.Control
            readOnly
            value={incoming.organisasjonsnr || ''}
            isValid={Boolean(brregInfo)}
            isInvalid={Boolean(brregInfo?.finnesIkke)}
          />
          <InputGroup.Text>
            <CopyToClipboardIcon val={incoming.organisasjonsnr} />
          </InputGroup.Text>
        </InputGroup>
        {incoming.juridisknavn && (
          <div className={Style.orgNrFormRow}>
            <div className={Style.formTxtCol}>
              <div>
                <Form.Text className={nameMismatch ? 'text-danger' : 'text-success'}>
                  <span
                    title={
                      nameMismatch
                        ? 'Ikke likt med juridisk navn i brreg'
                        : 'Juridisk navn verifisert mot Brreg'
                    }
                  >
                    Juridisk navn i endringsmelding: {incoming.juridisknavn}
                  </span>
                </Form.Text>
                {nameMismatch && (
                  <>
                    <br />
                    <Form.Text className={nameMismatch ? 'text-danger' : 'text-muted'}>
                      Juridisk navn i brreg: {brregInfo?.name}
                    </Form.Text>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {typeof enhetCount === 'number' && (
          <Form.Text className="d-block mt-1" data-testid="incoming-enhet-count-msg">
            {(() => {
              const filter = new EnhetSearchFilter();
              const criteria = currentOrgnr ?? '';
              filter.criteria = criteria;
              const searchstring = querystring.stringify(filter, {
                arrayFormat: 'bracket',
                skipNull: true,
              });
              const url = `/enheter?${searchstring}`;
              // The count only includes this enhet itself when its own saved orgnr already
              // matches the incoming value (compareStatus 'lik'). If the incoming value differs
              // from what's saved, every hit belongs to some other enhet.
              const otherCount = compareStatus === 'lik' ? Math.max(enhetCount - 1, 0) : enhetCount;
              return (
                <>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Åpne enhetssøk for orgnr ${criteria}`}
                  >
                    {otherCount > 0 ? `${otherCount} andre enheter` : 'Ingen andre enheter'} med
                    dette organisasjonsnummeret
                  </a>
                </>
              );
            })()}
          </Form.Text>
        )}
      </Form.Group>
    </Col>
  );
};

export default OrgNrLeft;
